import React from 'react';
import { Link } from 'react-router-dom';
import DashboardCard from './DashboardCard';

export interface AttentionItem {
  id: string;
  title: string;
  description: string;
  ctaLabel: string;
  to: string;
  tone?: 'default' | 'warning' | 'critical';
}

interface DashboardAttentionSectionProps {
  items: AttentionItem[];
  /** Section heading (default: conversion guidance) */
  heading?: string;
  intro?: string | null;
  /** Right-aligned header action, mirroring RankedPipelineList's "view all" link. */
  viewAllTo?: string;
  viewAllLabel?: string;
  /** Grid span / layout classes only. */
  className?: string;
}

const toneAccent: Record<NonNullable<AttentionItem['tone']>, string> = {
  default: 'border-l-primary',
  warning: 'border-l-amber-500',
  critical: 'border-l-red-500',
};

const DashboardAttentionSection: React.FC<DashboardAttentionSectionProps> = ({
  items,
  heading = 'What needs attention today',
  intro,
  viewAllTo = '/leads',
  viewAllLabel = 'Review leads',
  className,
}) => {
  if (!items.length) return null;

  return (
    <DashboardCard
      title={heading}
      subtitle={intro ?? undefined}
      action={
        <Link
          to={viewAllTo}
          className="text-sm font-medium text-primary hover:text-primary-dark shrink-0"
        >
          {viewAllLabel}
        </Link>
      }
      className={className}
      scrollBody
    >
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.id}>
            <div
              className={`p-3 rounded-md border border-border border-l-4 ${toneAccent[item.tone ?? 'default']} hover:bg-app/80 transition-colors`}
            >
              <p className="text-sm font-semibold text-fg">{item.title}</p>
              <p className="text-sm text-fg-secondary mt-1">{item.description}</p>
              <Link
                to={item.to}
                className="inline-block mt-2 text-sm font-medium text-primary hover:text-primary-dark"
              >
                {item.ctaLabel} →
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </DashboardCard>
  );
};

export default DashboardAttentionSection;
